/**
 * Blog posts tabs
 *
 * Progressively enhances the featured blog posts section into a tabbed view.
 * The section renders one `.blog-posts-tabs__panel` per blog (each tagged with a
 * `data-blog-handle`) and the merchant's own buttons link to those blogs. Without
 * JS the first panel shows and the buttons navigate to the blog pages; with JS the
 * buttons become tabs that swap the visible panel in place.
 */
class BlogPostsTabs extends HTMLElement {
  connectedCallback() {
    this.panels = Array.from(this.querySelectorAll('.blog-posts-tabs__panel'));
    if (this.panels.length < 2) return;

    const handles = new Set(this.panels.map((panel) => panel.dataset.blogHandle).filter(Boolean));

    // Tab triggers are blog links that live outside the panels (e.g. the heading
    // buttons) and point at one of the rendered blogs. Card links inside the panels
    // also point at `/blogs/...`, so they are explicitly excluded.
    this.triggers = Array.from(this.querySelectorAll('a[href*="/blogs/"]'))
      .filter((el) => !el.closest('.blog-posts-tabs__panel'))
      .map((el) => ({ el, handle: this.#handleFromHref(el.getAttribute('href')) }))
      .filter(({ handle }) => handle && handles.has(handle));

    if (this.triggers.length < 2) return;

    for (const { el, handle } of this.triggers) {
      el.setAttribute('role', 'tab');
      el.addEventListener('click', (event) => {
        event.preventDefault();
        this.#activate(handle);
      });
    }

    const initialPanel = this.panels.find((panel) => !panel.hasAttribute('hidden')) ?? this.panels[0];
    this.#activate(initialPanel.dataset.blogHandle);
  }

  /**
   * Extracts a blog handle from a blog URL, e.g. "/blogs/recipes" -> "recipes".
   *
   * @param {string | null} href
   * @returns {string | null}
   */
  #handleFromHref(href) {
    if (!href) return null;
    const tail = href.split('/blogs/')[1];
    if (!tail) return null;
    return tail.split(/[/?#]/)[0] || null;
  }

  /**
   * Shows the panel for the given blog handle and updates the trigger states.
   *
   * @param {string | undefined} handle
   */
  #activate(handle) {
    if (!handle) return;

    for (const panel of this.panels) {
      panel.toggleAttribute('hidden', panel.dataset.blogHandle !== handle);
    }

    for (const { el, handle: triggerHandle } of this.triggers) {
      const active = triggerHandle === handle;
      el.setAttribute('aria-selected', String(active));
      el.classList.toggle('button', active);
      el.classList.toggle('button-secondary', !active);
    }
  }
}

if (!customElements.get('blog-posts-tabs')) {
  customElements.define('blog-posts-tabs', BlogPostsTabs);
}
